import React, { useCallback, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import PlantillaScreen from "../../helpers/PlantillaScreen";
import { LoaderScreen } from "../../components/LoaderScreen";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { useBox } from "../../Finanzas/Helper/useBox";
// import FiltroReporteRefaccionesDetalleVendedor from "./FiltroReporteRefaccionesDetalleVendedor";
import { ChartFamiliasRefacciones } from "./ChartFamiliasRefacciones";
import { ChartVentaMensual } from "./ChartVentaMensual";
import { TablaPareto } from "./TablaPareto";
import { TablaKpisPorAnio } from "./TablaKpisPorAnio";
import { KpiCard } from "./KpiCard";
import { FiltroMultiSelect } from "./FiltroMultiSelect";
import { FormatMoney } from "../../../Helpers/FormatNumber";
import { CANTIDAD_MOSTRAR_OPCIONES } from "../../../Arrays/CantidadMostrarArray";
import {
    filtrarDetallePorVendedor,
    filtrarDetalleVendedorPorFiltros,
    filtrarDetallePorSidebar,
    filtrarDetallePorPartes,
    calcularKpisFamiliaDesdeDetalle,
    calcularKpisFamiliaPorAnio,
    agruparVentasPorClientePlano,
    agruparVentasPorNumeroParteYPeriodo,
    construirSeriesMensualesDesdeDetalle,
    obtenerOpcionesFamiliaDesdeDetalle,
    obtenerOpcionesSubFamiliaDesdeDetalle,
    obtenerOpcionesSubFamilia2DesdeDetalle,
    obtenerOpcionesClienteDesdeDetalle,
    obtenerOpcionesSucursalDesdeDetalle,
} from "../../../Helpers/ReporteRefaccionesAgregaciones";

// "Detalle por Vendedor": mismo patrón/estructura que Pantalla 2 (Detalle
// por Familia, ver ReporteRefaccionesDetalleFamilia.js) — KPIs, Pareto de
// Clientes, número de parte, dispersión mensual y KPIs por año — pero
// calculados sobre el `detalle` que reenvía la Pantalla 1 al hacer clic en
// una barra del tab "Ventas por Vendedor" (ver `onVendedorClick` en
// ReporteRefaccionesBoceto.js). Los cálculos de KPIs (`calcularKpisFamiliaDesdeDetalle`/
// `calcularKpisFamiliaPorAnio`) son genéricos (no dependen de "Familia"
// internamente) y se reutilizan tal cual, sin duplicar lógica.

const filtroVendedorVacio = () => ({
    familia: "TODOS",
    subFamilia: "TODOS",
    subFamilia2: "TODOS",
    cliente: "TODOS",
});

// Formato corto dd/mm/aaaa para mostrar el rango de fechas heredado en el
// encabezado (las fechas llegan en formato ISO yyyy-mm-dd desde FechaInput).
const formatFechaCorta = (isoDate) => {
    if (!isoDate) return "";
    const [anio, mes, dia] = isoDate.split("-");
    return `${dia}/${mes}/${anio}`;
};

const ReporteRefaccionesDetalleVendedor = () => {
    const history = useHistory();
    const { state } = useLocation();

    // Contexto heredado de la Pantalla 1; valores por defecto si se entra directo por URL.
    const hoyIso = new Date().toISOString().split("T")[0];
    const boxstatePadre = state?.boxstate || { fechaInicio: hoyIso, fechaFin: hoyIso };
    const vendedor = state?.vendedor || { id: null, nombre: "Vendedor no especificado" };
    const detalleHeredado = state?.detalle || [];

    const [loader, setLoader] = useState({
        mostrar: false,
        alerta: false,
        error: false,
        mensaje: "",
    });

    const [boxstate, setBoxState] = useBox({
        familia: "TODOS",
        subFamilia: "TODOS",
        subFamilia2: "TODOS",
        cliente: "TODOS",
    });

    // Filtro propio de esta pantalla; solo se actualiza al presionar "Cargar" (no en vivo).
    const [filtroVendedorAplicado, setFiltroVendedorAplicado] = useState(filtroVendedorVacio);

    const onExecuteFiltroVendedor = (nuevoFiltro) => {
        return new Promise((resolve) => {
            setFiltroVendedorAplicado(nuevoFiltro);
            resolve(nuevoFiltro);
        });
    };

    // Filtro de Sucursal (toolbar): multi-selección, filtra en memoria al instante.
    const [sidebarFiltros, setSidebarFiltros] = useState({ sucursal: [] });

    const onCambiarFiltroSidebar = (key, nuevaSeleccion) => {
        setSidebarFiltros((prev) => ({ ...prev, [key]: nuevaSeleccion }));
    };

    const onLimpiarFiltrosSidebar = () => setSidebarFiltros({ sucursal: [] });

    // "Venta por número de parte": cantidad y orden a mostrar (el propio
    // ChartFamiliasRefacciones.js ordena/recorta y da scroll).
    const [cantidadNumeroParte, setCantidadNumeroParte] = useState("10");
    const [ordenNumeroParte, setOrdenNumeroParte] = useState("desc");

    // Selección de una sola parte (clic selecciona, clic de nuevo
    // deselecciona; elegir otra reemplaza la anterior) para filtrar
    // dispersión mensual, KPIs por año y Pareto; la propia gráfica de
    // número de parte no se autofiltra. useCallback evita reiniciar su
    // canvas de echarts en cada render.
    const [parteSeleccionada, setParteSeleccionada] = useState(null);

    const onNumeroParteClick = useCallback((item) => {
        setParteSeleccionada((prev) => (prev === item.id ? null : item.id));
    }, []);

    const onLimpiarParteSeleccionada = () => setParteSeleccionada(null);

    // Toggle Monto/Cantidad: afecta KPIs, número de parte, dispersión mensual y Pareto.
    const [metricaVista, setMetricaVista] = useState("monto");

    // Año principal (Fecha Fin heredada): acota los KPIs a un solo año.
    const anioPrincipal = boxstatePadre.fechaFin
        ? boxstatePadre.fechaFin.split("-")[0]
        : String(new Date().getFullYear());

    // Detalle acotado a este Vendedor, antes del filtro propio (opciones dinámicas + base para filtrar).
    const detalleVendedor = useMemo(
        () => filtrarDetallePorVendedor(detalleHeredado, vendedor.nombre),
        [detalleHeredado, vendedor.nombre]
    );

    const opcionesFiltroVendedor = useMemo(
        () => ({
            familia: obtenerOpcionesFamiliaDesdeDetalle(detalleVendedor),
            subFamilia: obtenerOpcionesSubFamiliaDesdeDetalle(detalleVendedor),
            subFamilia2: obtenerOpcionesSubFamilia2DesdeDetalle(detalleVendedor),
            cliente: obtenerOpcionesClienteDesdeDetalle(detalleVendedor),
        }),
        [detalleVendedor]
    );

    // Opciones del filtro de Sucursal, sobre `detalleVendedor` (sin filtrar aún).
    const opcionesSucursal = useMemo(
        () => obtenerOpcionesSucursalDesdeDetalle(detalleVendedor),
        [detalleVendedor]
    );

    // Detalle final: Vendedor + filtro propio (Cargar) + Sucursal (instantáneo).
    const detalleFiltradoVendedor = useMemo(() => {
        const conFiltroPropio = filtrarDetalleVendedorPorFiltros(detalleVendedor, filtroVendedorAplicado);
        return filtrarDetallePorSidebar(conFiltroPropio, sidebarFiltros);
    }, [detalleVendedor, filtroVendedorAplicado, sidebarFiltros]);

    // `anioPrincipal` acota los KPIs a un solo año (la dispersión mensual usa todos).
    const kpisCalculados = useMemo(
        () => calcularKpisFamiliaDesdeDetalle(detalleFiltradoVendedor, anioPrincipal, metricaVista),
        [detalleFiltradoVendedor, anioPrincipal, metricaVista]
    );

    // Sin filtro de partes: debe seguir mostrando todas para poder seleccionarlas.
    // Desglosado por año (igual forma que ChartFamiliasRefacciones espera).
    const numeroParteDatos = useMemo(
        () => agruparVentasPorNumeroParteYPeriodo(detalleFiltradoVendedor, metricaVista),
        [detalleFiltradoVendedor, metricaVista]
    );

    // Detalle acotado además a la parte seleccionada; alimenta Pareto, KPIs por año y dispersión mensual.
    const detalleFiltradoPorPartes = useMemo(
        () => filtrarDetallePorPartes(detalleFiltradoVendedor, parteSeleccionada ? [parteSeleccionada] : []),
        [detalleFiltradoVendedor, parteSeleccionada]
    );

    const clientesParetoDatos = useMemo(
        () => agruparVentasPorClientePlano(detalleFiltradoPorPartes, metricaVista),
        [detalleFiltradoPorPartes, metricaVista]
    );

    // Años de la dispersión mensual: año principal + comparativos seleccionados.
    const aniosMostrados = useMemo(() => {
        const comparativos = (boxstatePadre.anio || []).map(String);
        const conjunto = new Set([...comparativos, anioPrincipal]);
        return [...conjunto].sort((a, b) => Number(a) - Number(b));
    }, [boxstatePadre.anio, anioPrincipal]);

    const seriesMensuales = useMemo(
        () => construirSeriesMensualesDesdeDetalle(detalleFiltradoPorPartes, aniosMostrados, metricaVista),
        [detalleFiltradoPorPartes, aniosMostrados, metricaVista]
    );

    // "KPIs por año" (junto a la dispersión mensual): los 5 KPIs desglosados
    // por año, igual patrón que Pantalla 2/3. Usa `detalleFiltradoPorPartes`
    // (reacciona a la parte seleccionada) y respeta `metricaVista`.
    const kpisPorAnioVendedor = useMemo(
        () => calcularKpisFamiliaPorAnio(detalleFiltradoPorPartes, aniosMostrados, metricaVista),
        [detalleFiltradoPorPartes, aniosMostrados, metricaVista]
    );

    const kpisConsolidadoVendedor = useMemo(
        () => calcularKpisFamiliaDesdeDetalle(detalleFiltradoPorPartes, undefined, metricaVista),
        [detalleFiltradoPorPartes, metricaVista]
    );

    // Drill-down a Pantalla 3 (360 del Cliente): reenvía `detalleHeredado`
    // completo (todos los Vendedores), no solo este Vendedor.
    const onClienteClick = (fila) => {
        return;
        // history.push({
        //     pathname: "/PostVenta/ReporteRefaccionesDetalleCliente",
        //     state: {
        //         boxstatePadre,
        //         cliente: { id: fila.id, nombre: fila.nombre },
        //         detalle: detalleHeredado,
        //         filtrosReporteRefacciones: state?.filtrosReporteRefacciones,
        //     },
        // });
    };

    // No usa history.goBack() (perdería el estado local de la Pantalla 1); navega reenviando filtros.
    const onRegresar = () => {
        history.push({
            pathname: "/PostVenta/ReporteRefacciones",
            state: { filtrosReporteRefacciones: state?.filtrosReporteRefacciones },
        });
    };

    return (
        <PlantillaScreen
            titulo={`DETALLE VENDEDOR: ${vendedor.nombre.toUpperCase()}`}
            className={"plantilla-detalle-vendedor"}
            className_main={1}
        >
            <LoaderScreen loader={loader} />
            <div className="peb-actions">
                <div className="peb-actions-regresar" onClick={onRegresar}>
                    <ArrowBackIcon /> Regresar
                </div>
                {/*
                <div className="peb-actions-filtro">
                    <FiltroReporteRefaccionesDetalleVendedor
                        boxState={boxstate}
                        onBoxChange={setBoxState}
                        onExecute={onExecuteFiltroVendedor}
                        opciones={opcionesFiltroVendedor}
                    />
                </div> */}

                <div className="peb-actions-contexto">
                    Del {formatFechaCorta(boxstatePadre.fechaInicio)} al{" "}
                    {formatFechaCorta(boxstatePadre.fechaFin)}
                </div>
            </div>

            {/* Barra de herramientas: toggle Monto/Cantidad + filtro de Sucursal. */}
            <div className="pdv-toolbar">
                <div className="pdv-metrica-toggle">
                    <button
                        type="button"
                        className={`pdv-metrica-boton${metricaVista === "monto" ? " pdv-metrica-boton--activo" : ""}`}
                        onClick={() => setMetricaVista("monto")}
                    >
                        Monto
                    </button>
                    <button
                        type="button"
                        className={`pdv-metrica-boton${metricaVista === "cantidad" ? " pdv-metrica-boton--activo" : ""}`}
                        onClick={() => setMetricaVista("cantidad")}
                    >
                        Cantidad
                    </button>
                </div>

                <div className="pdv-toolbar-sucursal">
                    <div className="pdv-toolbar-sucursal-campo">
                        <FiltroMultiSelect
                            label="Sucursal"
                            options={opcionesSucursal}
                            selected={sidebarFiltros.sucursal}
                            onChange={(nuevaSeleccion) => onCambiarFiltroSidebar("sucursal", nuevaSeleccion)}
                            flotante
                        />
                    </div>
                    {sidebarFiltros.sucursal.length > 0 && (
                        <div className="pdv-toolbar-sucursal-limpiar" onClick={onLimpiarFiltrosSidebar}>
                            Limpiar
                        </div>
                    )}
                </div>
            </div>

            <div className="pdv-main-content scroll">
                {/* <div className="pdv-kpis">
                    <KpiCard
                        titulo={metricaVista === "cantidad" ? "Cantidad total" : "Venta total"}
                        valor={
                            metricaVista === "cantidad"
                                ? `${Math.round(kpisCalculados.ventaTotal).toLocaleString("es-MX")} unidades`
                                : `$${FormatMoney(kpisCalculados.ventaTotal, 0)}`
                        }
                    />
                    <KpiCard titulo="Facturas" valor={kpisCalculados.facturasTotales.toLocaleString("es-MX")} />
                    <KpiCard
                        titulo={metricaVista === "cantidad" ? "Cantidad por factura" : "Factura promedio"}
                        valor={
                            metricaVista === "cantidad"
                                ? `${Math.round(kpisCalculados.facturaPromedio).toLocaleString("es-MX")} unidades`
                                : `$${FormatMoney(kpisCalculados.facturaPromedio, 0)}`
                        }
                    />
                    <KpiCard titulo="Margen" valor={`${(kpisCalculados.margen * 100).toFixed(1)}%`} />
                </div> */}

                <div className="pdv-mensual">
                    <div className="pdv-mensual-chart-container">
                        <div className="pdv-mensual-title">
                            Dispersión de {metricaVista === "cantidad" ? "cantidad" : "venta"} mensual
                            {aniosMostrados.length > 1 ? " (comparativo por año)" : ""}
                            {parteSeleccionada ? ` — filtrado por la parte ${parteSeleccionada}` : ""}
                        </div>
                        {/* Wrapper flex necesario para que el chart no tome el 100% del contenedor, dejando espacio al título. */}
                        <div className="pdv-mensual-chart-wrapper">
                            <ChartVentaMensual series={seriesMensuales} metrica={metricaVista} />
                        </div>
                    </div>

                    {/* KPIs por año: mismo componente/patrón que Pantalla 2/3. */}
                    <div className="pdv-mensual-kpis-container">
                        <div className="pdv-mensual-title">
                            KPIs por año
                            {parteSeleccionada ? ` — filtrado por la parte ${parteSeleccionada}` : ""}
                        </div>
                        <TablaKpisPorAnio
                            filas={kpisPorAnioVendedor}
                            anioPrincipal={anioPrincipal}
                            consolidado={kpisConsolidadoVendedor}
                            metrica={metricaVista}
                        />
                    </div>
                </div>

                <div className="pdv-body">
                    <div className="pdv-body-chart">
                        <div className="pdv-body-chart-container">
                            <div className="pdv-numparte-header">
                                <div className="pdv-body-title">
                                    {metricaVista === "cantidad" ? "Cantidad por número de parte" : "Venta por número de parte"}
                                </div>
                                <div className="pdv-numparte-controles">
                                    <select
                                        className="pdv-numparte-cantidad"
                                        value={cantidadNumeroParte}
                                        onChange={({ target }) => setCantidadNumeroParte(target.value)}
                                    >
                                        {CANTIDAD_MOSTRAR_OPCIONES.map((opcion) => (
                                            <option key={opcion} value={opcion}>
                                                {opcion === "todos" ? "Todos" : opcion}
                                            </option>
                                        ))}
                                    </select>
                                    <div
                                        className="pdv-numparte-orden"
                                        title={
                                            ordenNumeroParte === "desc"
                                                ? "Mostrando de mayor a menor. Clic para invertir."
                                                : "Mostrando de menor a mayor. Clic para invertir."
                                        }
                                        onClick={() =>
                                            setOrdenNumeroParte((prev) => (prev === "desc" ? "asc" : "desc"))
                                        }
                                    >
                                        {ordenNumeroParte === "desc" ? <ArrowDownwardIcon /> : <ArrowUpwardIcon />}
                                    </div>
                                </div>
                            </div>

                            {/* Solo visible con una parte seleccionada; dispersión mensual, KPIs por año y Pareto ya la reflejan. */}
                            {parteSeleccionada && (
                                <div className="pdv-numparte-seleccion">
                                    <span>Parte seleccionada: {parteSeleccionada}</span>
                                    <span
                                        className="pdv-numparte-seleccion-limpiar"
                                        onClick={onLimpiarParteSeleccionada}
                                    >
                                        Limpiar
                                    </span>
                                </div>
                            )}

                            <ChartFamiliasRefacciones
                                id="chart-numero-parte-refacciones-vendedor"
                                datos={numeroParteDatos}
                                anios={aniosMostrados}
                                anioPrincipal={anioPrincipal}
                                cantidad={cantidadNumeroParte}
                                orden={ordenNumeroParte}
                                onItemClick={onNumeroParteClick}
                                seleccionados={parteSeleccionada ? [parteSeleccionada] : []}
                                metrica={metricaVista}
                            />
                        </div>
                    </div>
                    <div className="pdv-body-pareto">
                        <TablaPareto
                            titulo={
                                parteSeleccionada
                                    ? `Pareto de Clientes (80/20) — filtrado por la parte ${parteSeleccionada}`
                                    : "Pareto de Clientes (80/20)"
                            }
                            columnaNombre="Cliente"
                            datos={clientesParetoDatos}
                            onRowClick={onClienteClick}
                            metrica={metricaVista}
                        />
                    </div>
                </div>
            </div>
        </PlantillaScreen>
    );
};

export default ReporteRefaccionesDetalleVendedor;
